// Shared pieces for pasting real content into a tiptap editor - a spreadsheet as a table,
// a screenshot as a hosted image.
//
// There are three separate tiptap instances in this app (the quick quote builder, the
// mailbox reply, and the terms dialog through the first of those). Each keeps its own
// extension list, so a fix applied to one silently leaves the others behind - which is how
// pasting a table came to work in a quote and not in a reply. Everything either of them
// needs to accept a paste lives here so there is one copy to fix.

import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import { Extension } from '@tiptap/core';
import { keptStyleOf, resolveStyleBlocks } from './richPaste.styles.js';

const styleAttribute = {
    style: {
        default: null,
        parseHTML: element => keptStyleOf(element),
        renderHTML: attributes => (attributes.style ? { style: attributes.style } : {}),
    },
};

const StyledTableCell = TableCell.extend({ addAttributes() { return { ...this.parent?.(), ...styleAttribute }; } });
const StyledTableHeader = TableHeader.extend({ addAttributes() { return { ...this.parent?.(), ...styleAttribute }; } });
const StyledTableRow = TableRow.extend({ addAttributes() { return { ...this.parent?.(), ...styleAttribute }; } });

// Text colour lives on a span rather than the cell, so it needs the same treatment as
// font size does.
const TextColor = Extension.create({
    name: 'textColor',
    addOptions() { return { types: ['textStyle'] }; },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                color: {
                    default: null,
                    parseHTML: element => element.style.color || null,
                    renderHTML: attributes => (attributes.color ? { style: `color: ${attributes.color}` } : {}),
                },
            },
        }];
    },
});

// Add to any editor's extension list to let it accept tables and images.
const richPasteExtensions = [
    Table.configure({ resizable: false }),
    StyledTableRow,
    StyledTableHeader,
    StyledTableCell,
    // allowBase64 covers the moment between the paste and the upload finishing, and images
    // pasted as HTML from elsewhere.
    Image.configure({ inline: false, allowBase64: true }),
    TextColor,
];

// Outlook renders mail through Word, which sizes a remote image from its width/height
// attributes and mishandles one carrying neither. Capped to a usual mail content width: a
// retina screenshot is often twice as wide as the message it is pasted into.
const MAX_PASTED_IMAGE_WIDTH = 600;

function measureImage(dataUrl) {
    return new Promise(resolve => {
        // window.Image - the tiptap node is imported under that name in this module.
        const probe = new window.Image();
        probe.onload = () => {
            const naturalWidth = probe.naturalWidth || 0;
            const naturalHeight = probe.naturalHeight || 0;
            if (!naturalWidth || !naturalHeight) return resolve({});
            const scale = Math.min(1, MAX_PASTED_IMAGE_WIDTH / naturalWidth);
            resolve({ width: Math.round(naturalWidth * scale), height: Math.round(naturalHeight * scale) });
        };
        probe.onerror = () => resolve({});
        probe.src = dataUrl;
    });
}

function readAsDataUrl(file) {
    return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
    });
}

/**
 * Show the image immediately as base64 so the paste feels instant, then swap in the hosted
 * URL once it uploads - a quote carrying a full screenshot inline would bloat the record it
 * is stored in, and base64 images are unreliable in mail clients. Without an uploader the
 * base64 stays, which still beats a paste that does nothing.
 */
async function insertPastedImage({ editor, file, onUploadImage, onChanged }) {
    const dataUrl = await readAsDataUrl(file);
    if (!dataUrl || !editor) return;

    if (!editor.schema.nodes.image) {
        console.error('[richPaste] No image node in this editor\'s schema - if it was just hot reloaded, reload the page.');
        return;
    }

    const size = await measureImage(dataUrl);
    editor.chain().focus().insertContent({ type: 'image', attrs: { src: dataUrl, ...size } }).run();

    if (onUploadImage) {
        try {
            const url = await onUploadImage({ dataUrl, contentType: file.type });
            if (url) {
                // The caret has moved on, so find the placeholder by its src rather than
                // assuming where it landed.
                editor.state.doc.descendants((node, pos) => {
                    if (node.type.name !== 'image' || node.attrs.src !== dataUrl) return;
                    editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, node.type, { ...node.attrs, src: url }));
                });
            }
        } catch (e) {
            console.error('[richPaste] Image upload failed, keeping the inline copy:', e);
        }
    }
    if (onChanged) onChanged({ html: editor.getHTML() });
}

/**
 * A paste handler for editorProps. Markup always wins: Excel, Word and Numbers put a
 * picture of the selection on the clipboard beside the real HTML, and taking the image
 * because it is there would turn every pasted spreadsheet into a screenshot of itself. An
 * image is only taken when the clipboard carries nothing else, which is what a genuine
 * screenshot looks like.
 */
function createImagePasteHandler({ getEditor, onUploadImage, onChanged }) {
    return (view, event) => {
        const clipboard = event.clipboardData;
        const html = (clipboard?.getData('text/html') || '').trim();
        const text = (clipboard?.getData('text/plain') || '').trim();
        if (html || text) return false;

        const files = [...(clipboard?.files || [])].filter(f => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach(file => insertPastedImage({ editor: getEditor(), file, onUploadImage, onChanged }));
        return true;
    };
}

export { richPasteExtensions, createImagePasteHandler, insertPastedImage, resolveStyleBlocks };
